/**
 * Referral code persistence helpers.
 *
 * The customer-facing share link is `forgeledger.net/?ref=ABCD1234`. When
 * a visitor lands on the site we stash the code in localStorage so that
 * even if they bounce off, browse the blog, and come back days later,
 * their eventual signup still attributes the referral correctly.
 *
 * Expires after 30 days — long enough for "I'll read more later" but
 * short enough that an abandoned device doesn't carry a stale code
 * across account creations.
 */
const STORAGE_KEY = 'forge:ref_code';
const STORAGE_TIMESTAMP_KEY = 'forge:ref_code_at';
const TTL_MS = 30 * 24 * 60 * 60 * 1000; // 30 days

/** Validate the code shape so a malicious URL can't poison localStorage with garbage. */
const isValidCode = (raw) => /^[A-Z2-9]{8}$/.test((raw || '').toUpperCase());

/**
 * Capture the ?ref= query param from the current URL into localStorage.
 * No-op if the URL doesn't have one or the value is malformed. Called
 * once on app boot.
 */
export const captureReferralFromUrl = () => {
  try {
    const params = new URLSearchParams(window.location.search);
    const raw = (params.get('ref') || '').toUpperCase();
    if (!raw || !isValidCode(raw)) return;
    localStorage.setItem(STORAGE_KEY, raw);
    localStorage.setItem(STORAGE_TIMESTAMP_KEY, String(Date.now()));
  } catch (_e) {
    // Private-browsing / SSR — non-fatal.
  }
};

/**
 * Return the stored ref code if it's still within the 30-day window,
 * otherwise clear it and return null.
 */
export const getStoredReferral = () => {
  try {
    const code = localStorage.getItem(STORAGE_KEY);
    const at = Number(localStorage.getItem(STORAGE_TIMESTAMP_KEY) || 0);
    if (!code || !isValidCode(code)) return null;
    if (!at || Date.now() - at > TTL_MS) {
      localStorage.removeItem(STORAGE_KEY);
      localStorage.removeItem(STORAGE_TIMESTAMP_KEY);
      return null;
    }
    return code;
  } catch (_e) {
    return null;
  }
};

/**
 * Wipe the ref code (called after a successful signup so it doesn't
 * accidentally attach to a future second account on the same device).
 */
export const clearStoredReferral = () => {
  try {
    localStorage.removeItem(STORAGE_KEY);
    localStorage.removeItem(STORAGE_TIMESTAMP_KEY);
  } catch (_e) {
    // Non-fatal.
  }
};
